import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import SignaturePad from "@/components/SignaturePad";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, ArrowRight, Send, CheckCircle2, FileSignature } from "lucide-react";
import { toast } from "sonner";

export default function EstimateDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [signOpen, setSignOpen] = useState(false);
  const [signedBy, setSignedBy] = useState("");
  const sigRef = useRef(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/estimates/${id}`);
    setData(data);
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendEstimate = async () => {
    try {
      const { data } = await api.post(`/estimates/${id}/send`, { origin_url: window.location.origin });
      if (data.sent) {
        toast.success(`Estimate emailed to ${data.to}`);
      } else {
        toast.message(data.message || "Email not sent", { description: `Share this link: ${data.view_url}` });
      }
      await load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const saveSignature = async () => {
    if (!signedBy.trim()) { toast.error("Enter customer name"); return; }
    if (sigRef.current?.isEmpty?.()) { toast.error("Please sign first"); return; }
    setSaving(true);
    try {
      const sigData = sigRef.current.getDataURL();
      await api.put(`/estimates/${id}`, { status: "approved", signature_data: sigData, signed_by: signedBy });
      toast.success("Estimate approved!");
      setSignOpen(false);
      await load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const convertToJob = async () => {
    try {
      const { data: r } = await api.post(`/estimates/${id}/convert-to-job`);
      toast.success(r.already_converted ? "Opening linked job…" : "Converted to job");
      nav(`/jobs/${r.job_id}`);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  if (!data) {
    return <div className="h-40 rounded-2xl bg-white border border-slate-200 animate-pulse" />;
  }
  const { estimate, customer } = data;
  const isApproved = estimate.status === "approved";
  const canConvert = isApproved && !estimate.job_id;

  return (
    <div className="space-y-5 max-w-3xl">
      <Link to="/estimates" className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 font-semibold">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back
      </Link>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs text-slate-500 font-bold uppercase tracking-wide">{estimate.number}</div>
            <h1 className="text-2xl font-bold mt-1" data-testid="estimate-title">{estimate.title}</h1>
            <p className="text-slate-500 text-sm mt-1">{customer?.name}</p>
          </div>
          <StatusBadge status={estimate.status} />
        </div>

        <div className="mt-4 divide-y divide-slate-100 border-y border-slate-100">
          {estimate.items.map((it, i) => (
            <div key={i} className="py-2.5 flex items-center justify-between gap-3 text-sm" data-testid={`est-row-${i}`}>
              <div className="min-w-0">
                <div className="font-medium truncate">{it.description}</div>
                <div className="text-xs text-slate-500">{it.quantity} × ${Number(it.unit_price).toFixed(2)}</div>
              </div>
              <div className="font-semibold">${(Number(it.quantity) * Number(it.unit_price)).toFixed(2)}</div>
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center justify-between">
          <div className="text-slate-500 font-semibold">Total</div>
          <div className="text-2xl font-bold" style={{ fontFamily: "Outfit" }} data-testid="estimate-total">${(estimate.total_amount || 0).toFixed(2)}</div>
        </div>

        {estimate.signature_data && (
          <div className="mt-4 pt-4 border-t border-slate-200">
            <div className="text-xs text-slate-500 font-bold uppercase tracking-wide">Signed by</div>
            <div className="text-sm font-semibold">{estimate.signed_by}</div>
            <img src={estimate.signature_data} alt="signature" className="mt-2 max-h-24 bg-slate-50 border border-slate-200 rounded-lg p-1" data-testid="signature-image" />
          </div>
        )}

        {(estimate.preferred_date || estimate.preferred_time) && (
          <div className="mt-4 pt-4 border-t border-slate-200 flex items-center gap-2 text-sm" data-testid="preferred-slot">
            <span className="text-xs text-slate-500 font-bold uppercase tracking-wide">Preferred slot</span>
            <span className="font-semibold">{estimate.preferred_date}{estimate.preferred_time && ` at ${estimate.preferred_time}`}</span>
          </div>
        )}
      </div>

      <div className="flex gap-2 flex-wrap">
        {(estimate.status === "draft" || estimate.status === "sent") && (
          <Button onClick={sendEstimate} className="h-11 px-4 bg-blue-600 hover:bg-blue-700 font-semibold rounded-xl" data-testid="send-estimate">
            <Send className="w-4 h-4 mr-1.5" /> Email to Customer
          </Button>
        )}
        {!isApproved && (
          <Button onClick={() => setSignOpen(true)} className="h-11 px-4 bg-green-600 hover:bg-green-700 font-semibold rounded-xl" data-testid="get-signature">
            <FileSignature className="w-4 h-4 mr-1.5" /> Get Customer Signature
          </Button>
        )}
        {canConvert && (
          <Button onClick={convertToJob} className="h-11 px-4 bg-slate-900 hover:bg-slate-800 font-semibold rounded-xl" data-testid="convert-to-job">
            <ArrowRight className="w-4 h-4 mr-1.5" /> Convert to Job
          </Button>
        )}
        {estimate.job_id && (
          <Link to={`/jobs/${estimate.job_id}`}>
            <Button className="h-11 px-4 bg-slate-900 hover:bg-slate-800 font-semibold rounded-xl">
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> View Linked Job
            </Button>
          </Link>
        )}
      </div>

      <Dialog open={signOpen} onOpenChange={setSignOpen}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader><DialogTitle>Customer Signature</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm font-semibold">Customer's Full Name</Label>
              <Input value={signedBy} onChange={(e) => setSignedBy(e.target.value)} className="h-12 mt-1.5" data-testid="signed-by" />
            </div>
            <div>
              <Label className="text-sm font-semibold">Sign here</Label>
              <div className="mt-1.5">
                <SignaturePad ref={sigRef} />
              </div>
            </div>
            <Button onClick={saveSignature} disabled={saving} className="w-full h-12 bg-green-600 hover:bg-green-700 font-semibold rounded-xl" data-testid="save-signature">
              {saving ? "Saving…" : "Approve Estimate"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
